import { cleanTypedNumber } from "@clinic/shared";
import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type JSX,
  type ReactNode,
} from "react";

export const cx = (...parts: (string | false | undefined | null)[]): string =>
  parts.filter(Boolean).join(" ");

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-primary-600 text-ink-inverse hover:bg-primary-700 active:bg-primary-800",
  secondary: "bg-inset text-ink hover:bg-neutral-200 active:bg-neutral-300",
  ghost: "text-primary-700 hover:bg-primary-50",
  danger: "bg-danger-600 text-ink-inverse hover:bg-danger-700 active:bg-danger-800",
};

export function Glyph({
  path,
  weight = 1.75,
  filled = false,
  className,
}: {
  readonly path: string;
  readonly weight?: number;
  readonly filled?: boolean;
  readonly className?: string;
}): JSX.Element {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("size-4 shrink-0", className)}
    >
      <path d={path} />
    </svg>
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly busy?: boolean;
  readonly full?: boolean;
}

export function Button({
  variant = "primary",
  busy = false,
  full = false,
  className,
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps): JSX.Element {
  const inert = disabled === true || busy;

  return (
    <button
      type="button"
      className={cx(
        "pill-text inline-flex items-center min-h-(--control-h) cursor-pointer justify-center gap-2 rounded-control",
        "px-5 text-field font-medium transition-colors duration-150",
        "aria-disabled:cursor-not-allowed aria-disabled:opacity-45",
        BUTTON_VARIANTS[variant],
        full && "w-full",
        className,
      )}
      {...(inert && { "aria-disabled": true })}
      {...(busy && { "aria-busy": true })}
      {...rest}
      onClick={(event) => {
        if (inert) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        onClick?.(event);
      }}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

function Spinner(): JSX.Element {
  return (
    <span
      aria-hidden
      className="size-4 animate-spin rounded-pill border-2 border-current border-t-transparent"
    />
  );
}

export function Card({
  children,
  className,
  "data-testid": testId,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly "data-testid"?: string | undefined;
}): JSX.Element {
  return (
    <section
      data-testid={testId}
      className={cx("border border-line rounded-card bg-surface p-4 shadow-card", className)}
    >
      {children}
    </section>
  );
}

export function ChoiceCard({
  selected,
  onClick,
  label,
  children,
  layout = "row",
  "data-testid": testId,
}: {
  readonly selected: boolean;
  readonly onClick: () => void;
  readonly label: string;
  readonly children: ReactNode;
  readonly layout?: "row" | "tile";
  readonly "data-testid"?: string | undefined;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={selected}
      aria-label={label}
      onClick={onClick}
      className={cx(
        "flex w-full cursor-pointer items-center gap-3 rounded-card p-4",
        layout === "tile" ? "h-full flex-col justify-center text-center" : "text-start",
        "transition-[background-color,box-shadow,scale] duration-150 active:scale-[0.98]",
        selected
          ? "bg-selected ring-2 ring-primary-600"
          : "border border-line bg-surface shadow-card hover:bg-row-hover",
      )}
    >
      {children}
    </button>
  );
}

export function Alert({
  tone = "danger",
  children,
  "data-testid": testId = "booking-alert",
}: {
  readonly tone?: "danger" | "info";
  readonly children: ReactNode;
  readonly "data-testid"?: string | undefined;
}): JSX.Element {
  return (
    <p
      role="status"
      data-testid={testId}
      className={cx(
        "rounded-panel px-3 py-2.5 text-value",
        tone === "danger" ? "bg-danger-50 text-danger-800" : "bg-primary-50 text-primary-900",
      )}
    >
      {children}
    </p>
  );
}

export function Skeleton({ className }: { readonly className?: string }): JSX.Element {
  return <span aria-hidden className={cx("skeleton block rounded-panel", className)} />;
}

export function Img({
  src,
  alt,
  width,
  height,
  priority = false,
  fit = "contain",
  fallback,
  "data-testid": testId,
}: {
  readonly "data-testid"?: string | undefined;
  readonly src: string | null | undefined;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
  readonly priority?: boolean;
  readonly fit?: "contain" | "cover";
  readonly fallback?: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<"loading" | "loaded" | "failed">("loading");
  const image = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const current = image.current;

    setState(current?.complete && current.naturalWidth > 0 ? "loaded" : "loading");
  }, [src]);

  const missing = src === null || src === undefined || src === "";

  return (
    <span
      style={{ width, height }}
      data-testid={testId}
      className="relative block max-w-full shrink-0 overflow-hidden"
    >
      {!missing && (
        <img
          ref={image}
          src={src}
          alt={alt}
          onLoad={() => setState("loaded")}
          onError={() => setState("failed")}
          {...(priority ? { loading: "eager", fetchPriority: "high" } : { loading: "lazy" })}
          decoding="async"
          className={cx(
            "size-full transition-opacity duration-[120ms]",
            fit === "cover" ? "object-cover" : "object-contain",
            state === "loaded" ? "opacity-100" : "opacity-0",
          )}
        />
      )}

      {!missing && state === "loading" && (
        <Skeleton className="absolute inset-0 size-full rounded-none" />
      )}

      {(missing || state === "failed") && fallback}
    </span>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly "data-testid"?: string | undefined;
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly prefix?: string | undefined;
  readonly addon?: ReactNode;
}

export function Field({
  label,
  error,
  hint,
  prefix,
  addon,
  id,
  className,
  "data-testid": testId,
  onChange,
  ...rest
}: FieldProps): JSX.Element {
  const fieldId = id ?? `field-${rest.name ?? label}`;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const describedBy = cx(error && errorId, hint && hintId) || undefined;
  const shellTone = error
    ? "border-danger-600 shadow-field-error"
    : "border-neutral-500 hover:border-neutral-700 focus:border-primary-600 focus:shadow-field-focus focus-within:border-primary-600 focus-within:shadow-field-focus";

  const input = (
    <input
      id={fieldId}
      data-testid={testId}
      className={cx(
        "min-h-(--control-h) w-full min-w-0 px-3 text-field text-ink",
        "placeholder:text-ink-subtle",
        prefix !== undefined || addon !== undefined
          ? "text-left"
          : rest.dir === "ltr"
            ? "page-rtl:text-right page-ltr:text-left"
            : "text-start",
        addon !== undefined
          ? "border-none bg-transparent"
          : cx("rounded-control border-[1.5px] bg-surface", shellTone),
        className,
      )}
      {...(describedBy && { "aria-describedby": describedBy })}
      {...(error && { "aria-invalid": true })}
      {...rest}
      onChange={(event) => {
        const cleaned = cleanTypedNumber(event.target.value, rest.inputMode);

        if (cleaned !== event.target.value) {
          event.target.value = cleaned;
        }

        onChange?.(event);
      }}
    />
  );

  return (
    <div data-testid={testId && `${testId}-field`} className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-value font-medium text-ink">
        {label}
      </label>

      {addon !== undefined ? (
        <div
          dir="ltr"
          className={cx(
            "flex min-h-(--control-h) items-center rounded-control border-[1.5px] bg-surface ps-1",
            shellTone,
          )}
        >
          {addon}
          <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-line" />
          {input}
        </div>
      ) : prefix === undefined ? (
        input
      ) : (
        <div dir="ltr" className="flex gap-2">
          <span
            data-testid={testId && `${testId}-prefix`}
            className="inline-flex min-h-(--control-h) shrink-0 items-center rounded-control border-[1.5px] border-line bg-sunken px-3 text-field tabular-nums text-ink-muted"
          >
            {prefix}
          </span>
          {input}
        </div>
      )}

      {hint && !error && (
        <span
          id={hintId}
          data-testid={testId && `${testId}-hint`}
          className="text-label text-ink-muted"
        >
          {hint}
        </span>
      )}

      {error && (
        <span
          id={errorId}
          role="alert"
          data-testid={testId && `${testId}-error`}
          className="text-label font-medium text-danger-700"
        >
          {error}
        </span>
      )}
    </div>
  );
}
